import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import customFetch from '@utils/customFetch';
import AdminListToolbar from '@components/dashboard/admin/shared/AdminListToolbar';
import Pagination from '@components/dashboard/admin/shared/Pagination';
import SortableHeader from '@components/dashboard/admin/shared/SortableHeader';
import StatusBadge, {
  statusFilterOptions,
} from '@components/dashboard/admin/shared/StatusBadge';
import useAdminListParams from '@/hooks/useAdminListParams';
import { formatChf, formatDate } from '@utils/format';

// Suche, Filter, Sortierung und Seite stehen in der URL — der Server liefert
// genau die angezeigte Seite. Vorher lud diese Maske alle Bestellungen und
// filterte im Speicher.
const allOrdersQuery = (searchParams) => ({
  queryKey: ['allOrders', searchParams.toString()],
  queryFn: async () => {
    const { data } = await customFetch.get(`/orders?${searchParams.toString()}`);
    return data;
  },
});

export const loader =
  (queryClient) =>
  async ({ request }) => {
    const { searchParams } = new URL(request.url);
    return queryClient.ensureQueryData(allOrdersQuery(searchParams));
  };

const COLUMN_COUNT = 7;

const OrderList = () => {
  const { searchParams, page, limit, search, sort, getParam, setParams, setPage, reset } =
    useAdminListParams({ defaultSort: 'createdAt:desc' });

  const { data, isLoading, isError } = useQuery(allOrdersQuery(searchParams));

  const orders = data?.allOrders ?? [];
  const totalOrders = data?.totalOrders ?? 0;
  const numOfPages = data?.numOfPages ?? 0;

  const handleSort = (value) => setParams({ sort: value });

  return (
    <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Bestellverwaltung
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {totalOrders} Bestellung{totalOrders === 1 ? '' : 'en'} gefunden.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <Link
            to="/dashboard/bestellbearbeitung/neu"
            className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400"
          >
            + Neue Bestellung
          </Link>
        </div>
      </div>

      <AdminListToolbar
        search={search}
        onSearchChange={(value) => setParams({ search: value })}
        searchPlaceholder="Bestellcode, Kunde, Tracking …"
        filters={[
          {
            key: 'status',
            label: 'Status',
            value: getParam('status'),
            options: statusFilterOptions('order', 'Alle Stati'),
            onChange: (value) => setParams({ status: value }),
          },
          {
            key: 'orderSource',
            label: 'Quelle',
            value: getParam('orderSource'),
            options: statusFilterOptions('orderSource', 'Alle Quellen'),
            onChange: (value) => setParams({ orderSource: value }),
          },
        ]}
        onReset={reset}
      >
        <div className="sm:w-40">
          <label
            htmlFor="order-from"
            className="block text-sm font-medium text-gray-900"
          >
            Von
          </label>
          <input
            id="order-from"
            type="date"
            value={getParam('from')}
            onChange={(event) => setParams({ from: event.target.value })}
            className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
          />
        </div>
        <div className="sm:w-40">
          <label
            htmlFor="order-to"
            className="block text-sm font-medium text-gray-900"
          >
            Bis
          </label>
          <input
            id="order-to"
            type="date"
            value={getParam('to')}
            onChange={(event) => setParams({ to: event.target.value })}
            className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
          />
        </div>
      </AdminListToolbar>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <SortableHeader
                    first
                    field="orderNumber"
                    label="Bestellcode"
                    sort={sort}
                    onSortChange={handleSort}
                  />
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Kunde
                  </th>
                  <SortableHeader
                    field="status"
                    label="Status"
                    sort={sort}
                    onSortChange={handleSort}
                  />
                  <SortableHeader
                    field="total"
                    label="Total"
                    sort={sort}
                    onSortChange={handleSort}
                  />
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Anzahl Karten
                  </th>
                  <SortableHeader
                    field="createdAt"
                    label="Datum"
                    sort={sort}
                    onSortChange={handleSort}
                  />
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                    <span className="sr-only">Bearbeiten</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading && (
                  <tr>
                    <td
                      colSpan={COLUMN_COUNT}
                      className="px-3 py-8 text-center text-sm text-gray-500"
                    >
                      Lädt …
                    </td>
                  </tr>
                )}

                {isError && !isLoading && (
                  <tr>
                    <td
                      colSpan={COLUMN_COUNT}
                      className="px-3 py-8 text-center text-sm text-red-600"
                    >
                      Bestellungen konnten nicht geladen werden.
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && orders.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLUMN_COUNT}
                      className="px-3 py-8 text-center text-sm text-gray-500"
                    >
                      Keine Bestellungen gefunden.
                    </td>
                  </tr>
                )}

                {orders.map((order) => (
                  <tr key={order._id} className="even:bg-gray-50">
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                      {order.orderNumber}
                      {order.trackingNumber && (
                        <span className="mt-0.5 block text-xs font-normal text-gray-500">
                          Tracking: {order.trackingNumber}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {order.owner?._id ? (
                        <Link
                          to={`/dashboard/benutzerverwaltung/${order.owner._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          {order.owner.firstName} {order.owner.lastName}
                          <span className="block text-xs text-gray-500">
                            {order.owner.email}
                          </span>
                        </Link>
                      ) : (
                        <span className="text-gray-400">Unbekannt</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge type="order" status={order.status} />
                        {order.orderSource === 'manual' && (
                          <StatusBadge type="orderSource" status="manual" />
                        )}
                        {order.orderSource === 'company' && (
                          <>
                            <StatusBadge type="orderSource" status="company" />
                            {order.company?.name && (
                              <span className="text-xs text-gray-500">
                                {order.company.name}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatChf(order.total)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {order.orderItems.reduce(
                        (sum, item) => sum + item.amount,
                        0
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                      <Link
                        to={`/dashboard/bestellbearbeitung/${order._id}`}
                        className="text-svisit-primary-600 hover:text-svisit-primary-900"
                      >
                        Bearbeiten
                        <span className="sr-only">, {order.orderNumber}</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              currentPage={page}
              numOfPages={numOfPages}
              total={totalOrders}
              limit={limit}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderList;
